import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const testimonials = [
  {
    name: "Alex Chen",
    role: "Engineer & Tesla Enthusiast",
    quote: "MuskConnect Pro changed how I engage with innovation. The community is incredible and the updates are invaluable.",
    avatar: "AC",
  },
  {
    name: "Sarah Martinez",
    role: "Entrepreneur",
    quote: "Being part of the investment program feels like I'm contributing to the future. Transparent, exciting, and inspiring.",
    avatar: "SM",
  },
  {
    name: "James Wilson",
    role: "Tech Student",
    quote: "The educational resources and live Q&As have shaped my career path. This is the community I needed.",
    avatar: "JW",
  },
  {
    name: "Priya Patel",
    role: "SpaceX Fan",
    quote: "Watching history in real-time with a community that shares my passion is unforgettable. Highly recommended!",
    avatar: "PP",
  },
  {
    name: "Marcus Johnson",
    role: "Investor",
    quote: "The Bitcoin rewards program is innovative. Real value, real transparency, real impact.",
    avatar: "MJ",
  },
];

export default function TestimonialCarousel() {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const next = () => setCurrent((prev) => (prev + 1) % testimonials.length);
  const prev = () => setCurrent((prev) => (prev - 1 + testimonials.length) % testimonials.length);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] },
    },
  };
  return (
    <motion.div
      className="space-y-8"
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
    >
      {/* Main Testimonial */}
      <motion.div
        key={current}
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -50 }}
        transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
      >
        <div className="border border-border p-8 md:p-12">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="text-lg text-foreground mb-8 leading-relaxed"
          >
            "{testimonials[current].quote}"
          </motion.p>
          <motion.div
            className="flex items-center gap-4"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            <Avatar className="h-12 w-12">
              <AvatarFallback className="text-sm font-medium bg-muted text-muted-foreground">
                {testimonials[current].avatar}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-medium text-foreground text-sm">
                {testimonials[current].name}
              </p>
              <p className="text-xs text-muted-foreground">
                {testimonials[current].role}
              </p>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Navigation */}
      <motion.div
        className="flex items-center justify-between"
        variants={itemVariants}
      >
        <motion.button
          onClick={prev}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          className="p-2 border border-border hover:border-foreground transition-colors"
        >
          <ChevronLeft className="h-5 w-5 text-foreground" />
        </motion.button>

        {/* Indicators */}
        <motion.div
          className="flex gap-2"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {testimonials.map((_, idx) => (
            <motion.button
              key={idx}
              onClick={() => setCurrent(idx)}
              whileHover={{ scale: 1.2 }}
              whileTap={{ scale: 0.9 }}
              className={`h-1.5 transition-all ${
                idx === current
                  ? "bg-foreground w-8"
                  : "bg-border w-1.5 hover:bg-muted"
              }`}
            />
          ))}
        </motion.div>

        <motion.button
          onClick={next}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          className="p-2 border border-border hover:border-foreground transition-colors"
        >
          <ChevronRight className="h-5 w-5 text-foreground" />
        </motion.button>
      </motion.div>
    </motion.div>
  );
}